import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, X } from 'lucide-react';
import { Alert, AlertDescription } from './Alert';

const commonFeedbackReasons = [
    'Samenvatting is onvolledig of mist belangrijke punten',
    'Samenvatting bevat onjuiste informatie',
    'Analyse mist belangrijke context',
    'Taalgebruik is onduidelijk of verwarrend',
    'Anders'
];

const AnalysisFeedback = ({ analysisId }) => {
    const [feedbackState, setFeedbackState] = useState('initial'); // initial, negative, submitted
    const [selectedReason, setSelectedReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmitFeedback = async (isPositive) => {
        if (isPositive) {
            setIsSubmitting(true);
            try {
                await submitFeedback({ analysisId, isPositive });
                setFeedbackState('submitted');
            } catch (error) {
                console.error('Error submitting feedback:', error);
            } finally {
                setIsSubmitting(false);
            }
        } else {
            setFeedbackState('negative');
        }
    };

    const handleSubmitNegativeFeedback = async () => {
        if (!selectedReason && !customReason) return;

        setIsSubmitting(true);
        try {
            await submitFeedback({ analysisId, isPositive: false, reason: selectedReason, customReason });
            setFeedbackState('submitted');
        } catch (error) {
            console.error('Error submitting feedback:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitFeedback = async (feedback) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/feedback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(feedback),
        });
        if (!response.ok) {
            throw new Error('Failed to submit feedback');
        }
    };

    if (feedbackState === 'submitted') {
        if (feedbackState === 'submitted') {
            return (
                <div className="mt-8">
                    <Alert className="bg-nn-teal-50 border-nn-teal-200">
                        <AlertDescription className="text-nn-teal-800">
                            Bedankt voor je feedback! Het helpt ons de analyses te verbeteren.
                        </AlertDescription>
                    </Alert>
                </div>
            );
        }

    }

    return (
        <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-nn-500">
                Was deze analyse behulpzaam?
            </h2>

            {feedbackState === 'initial' && (
                <div className="flex gap-4">
                    <button
                        onClick={() => handleSubmitFeedback(true)}
                        disabled={isSubmitting}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg border border-nn-gray-200 hover:bg-nn-gray-50 text-nn-gray-700 transition-colors disabled:opacity-50"
                    >
                        <ThumbsUp className="h-5 w-5" />
                        Ja
                    </button>
                    <button
                        onClick={() => handleSubmitFeedback(false)}
                        disabled={isSubmitting}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg border border-nn-gray-200 hover:bg-nn-gray-50 text-nn-gray-700 transition-colors disabled:opacity-50"
                    >
                        <ThumbsDown className="h-5 w-5" />
                        Nee
                    </button>
                </div>
            )}

            {feedbackState === 'negative' && (
                <div className="space-y-4">
                    <div className="space-y-3">
                        <label className="block text-sm font-medium text-nn-gray-700">
                            Wat kan er verbeterd worden?
                        </label>
                        <div className="space-y-2">
                            {commonFeedbackReasons.map((reason) => (
                                <div key={reason} className="flex items-center">
                                    <input
                                        type="radio"
                                        id={reason}
                                        name="feedbackReason"
                                        value={reason}
                                        checked={selectedReason === reason}
                                        onChange={(e) => setSelectedReason(e.target.value)}
                                        className="h-4 w-4 text-nn-500 focus:ring-nn-500 border-nn-gray-300"
                                    />
                                    <label htmlFor={reason} className="ml-2 text-sm text-nn-gray-700">
                                        {reason}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>

                    {selectedReason === 'Anders' && (
                        <div>
                            <textarea
                                value={customReason}
                                onChange={(e) => setCustomReason(e.target.value)}
                                placeholder="Geef hier je toelichting..."
                                className="mt-1 block w-full rounded-lg border-nn-gray-200 shadow-sm focus:border-nn-500 focus:ring-nn-500 sm:text-sm"
                                rows={3}
                            />
                        </div>
                    )}

                    <div className="flex gap-4 pt-2">
                        <button
                            onClick={handleSubmitNegativeFeedback}
                            disabled={isSubmitting || (!selectedReason && !customReason)}
                            className="px-4 py-2 bg-nn-500 text-white rounded-lg hover:bg-nn-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Verstuur feedback
                        </button>
                        <button
                            onClick={() => setFeedbackState('initial')}
                            className="px-4 py-2 text-nn-gray-700 hover:text-nn-gray-900"
                        >
                            Annuleren
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnalysisFeedback;